import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Globe,
  Link2,
  Copy,
  ExternalLink,
  QrCode,
  Check,
  X,
  Loader2,
  Sparkles,
  Clock,
  Shield,
  Palette,
  Crown,
  Download,
  Share2,
  Instagram,
  MessageSquare,
  Camera,
  Image as ImageIcon,
  Upload,
  Trash2,
  User,
  Plus,
  Megaphone
} from "lucide-react";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import QRCode from "react-qr-code";
import html2canvas from "html2canvas";
import BulletinBoard from "./BulletinBoard";

/**
 * MiniSiteSettings - Component for managing Pro Mini-Site settings
 */
const MiniSiteSettings = ({ token, membershipTier = "free", userName = "", userEmail = "" }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [slugChecking, setSlugChecking] = useState(false);
  const [slugAvailable, setSlugAvailable] = useState(null);
  const [generatingCard, setGeneratingCard] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingGallery, setUploadingGallery] = useState(false);
  const shareCardRef = useRef(null);
  const profileImageRef = useRef(null);
  const galleryInputRef = useRef(null);
  
  const [settings, setSettings] = useState({
    profile_slug: "",
    profile_image: "",
    gallery_images: [],
    welcome_message: "",
    signature_text: "",
    business_hours: {
      monday: "CLOSED",
      tuesday: "10:00 AM - 7:00 PM",
      wednesday: "10:00 AM - 7:00 PM",
      thursday: "9:00 AM - 7:00 PM",
      friday: "9:00 AM - 7:00 PM",
      saturday: "8:00 AM - 5:00 PM",
      sunday: "CLOSED"
    },
    booking_policies: {
      cancellation_notice: "2 hours",
      deposit_amount: 10,
      deposit_service_fee: 2,
      no_show_policy: "Full service charge",
      late_grace_period: 10,
      late_fee: 10,
      late_cancel_minutes: 15
    },
    social_links: {
      instagram: "",
      email: "",
      phone: ""
    },
    custom_branding: {
      primary_color: "#d4af37",
      secondary_color: "#dc2626",
      bg_color: "#000000"
    }
  });

  const [showQR, setShowQR] = useState(false);
  const [showShareCard, setShowShareCard] = useState(false);

  // Super Admin gets full access to all features
  const SUPER_ADMIN_EMAIL = "georgebroughton71@gmail.com";
  const isSuperAdmin = userEmail?.toLowerCase() === SUPER_ADMIN_EMAIL.toLowerCase();
  
  // Debug log - remove after fixing
  console.log("MiniSiteSettings - userEmail:", userEmail, "isSuperAdmin:", isSuperAdmin, "membershipTier:", membershipTier);
  
  const isPro = isSuperAdmin || membershipTier === "pro" || membershipTier === "elite";
  const isElite = isSuperAdmin || membershipTier === "elite";
  const baseUrl = window.location.origin;
  const fullUrl = settings.profile_slug ? `${baseUrl}/p/${settings.profile_slug}` : null;

  useEffect(() => {
    fetchSettings();
  }, [token]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/minisite/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(prev => ({
        ...prev,
        ...response.data,
        business_hours: response.data.business_hours || prev.business_hours,
        booking_policies: response.data.booking_policies || prev.booking_policies,
        social_links: response.data.social_links || prev.social_links,
        custom_branding: response.data.custom_branding || prev.custom_branding
      }));
    } catch (err) {
      console.error("Failed to fetch mini-site settings:", err);
    } finally {
      setLoading(false);
    }
  };

  const checkSlugAvailability = async (slug) => {
    if (!slug || slug.length < 3) {
      setSlugAvailable(null);
      return;
    }
    
    try {
      setSlugChecking(true);
      const response = await axios.get(`${API}/minisite/check-slug/${slug}`);
      setSlugAvailable(response.data.available);
    } catch (err) {
      setSlugAvailable(false);
    } finally {
      setSlugChecking(false);
    }
  };

  const handleSlugChange = (value) => {
    const slug = value.toLowerCase().replace(/[^a-z0-9._-]/g, '');
    setSettings(prev => ({ ...prev, profile_slug: slug }));
    
    // Debounce the availability check
    clearTimeout(window.slugCheckTimeout);
    window.slugCheckTimeout = setTimeout(() => checkSlugAvailability(slug), 500);
  };

  const saveSettings = async () => {
    try {
      setSaving(true);
      await axios.put(`${API}/minisite/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Mini-site settings saved!");
    } catch (err) {
      console.error("Failed to save settings:", err);
      toast.error(err.response?.data?.detail || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const copyUrl = () => {
    if (fullUrl) {
      navigator.clipboard.writeText(fullUrl);
      toast.success("Link copied to clipboard!");
    }
  };

  // Profile Image Upload
  const handleProfileImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error("Please select an image file");
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be less than 5MB");
      return;
    }
    
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("type", "profile");
      
      const response = await axios.post(`${API}/upload/image`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });
      
      setSettings(prev => ({ ...prev, profile_image: response.data.url }));
      toast.success("Profile image uploaded!");
    } catch (err) {
      console.error("Upload failed:", err);
      toast.error(err.response?.data?.detail || "Failed to upload image");
    } finally {
      setUploadingImage(false);
    }
  };

  // Gallery Image Upload
  const handleGalleryUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    
    const currentCount = settings.gallery_images?.length || 0;
    const remainingSlots = 6 - currentCount;
    
    if (files.length > remainingSlots) {
      toast.error(`You can only add ${remainingSlots} more image(s). Maximum is 6.`);
      return;
    }
    
    // Validate all files
    for (const file of files) {
      if (!file.type.startsWith('image/')) {
        toast.error("Please select only image files");
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Each image must be less than 5MB");
        return;
      }
    }
    
    setUploadingGallery(true);
    try {
      const uploadedUrls = [];
      
      for (const file of files) {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("type", "gallery");
        
        const response = await axios.post(`${API}/upload/image`, formData, {
          headers: { 
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data"
          }
        });
        
        uploadedUrls.push(response.data.url);
      }
      
      setSettings(prev => ({
        ...prev,
        gallery_images: [...(prev.gallery_images || []), ...uploadedUrls]
      }));
      toast.success(`${uploadedUrls.length} image(s) uploaded!`);
    } catch (err) {
      console.error("Gallery upload failed:", err);
      toast.error(err.response?.data?.detail || "Failed to upload images");
    } finally {
      setUploadingGallery(false);
    }
  };

  // Remove gallery image
  const removeGalleryImage = (index) => {
    setSettings(prev => ({
      ...prev,
      gallery_images: prev.gallery_images.filter((_, i) => i !== index)
    }));
    toast.success("Image removed");
  };

  // Remove profile image
  const removeProfileImage = () => {
    setSettings(prev => ({ ...prev, profile_image: "" }));
    toast.success("Profile image removed");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  if (!isPro) {
    // Import dynamically to avoid circular deps
    const ProMiniSitePreview = require("./ProMiniSitePreview").default;
    return <ProMiniSitePreview autoPlay={true} autoPlayInterval={4000} />;
  }

  return (
    <div className="space-y-6">
      {/* URL Configuration */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="w-5 h-5 text-amber-500" />
            Your Mini-Site URL
          </CardTitle>
          <CardDescription>
            Choose a custom URL for your professional mini-site
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-2">
            <span className="text-stone-500 font-mono text-sm">
              {baseUrl}/p/
            </span>
            <div className="relative flex-1">
              <Input
                value={settings.profile_slug}
                onChange={(e) => handleSlugChange(e.target.value)}
                placeholder="yourname"
                className="font-mono pr-10"
                data-testid="slug-input"
              />
              {slugChecking && (
                <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-stone-400" />
              )}
              {!slugChecking && slugAvailable === true && (
                <Check className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-green-500" />
              )}
              {!slugChecking && slugAvailable === false && (
                <X className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-red-500" />
              )}
            </div>
          </div>
          
          {slugAvailable === false && (
            <p className="text-sm text-red-500">This URL is not available</p>
          )}
          
          {fullUrl && slugAvailable !== false && (
            <div className="flex items-center gap-2 p-3 bg-stone-50 rounded-lg">
              <Link2 className="w-4 h-4 text-stone-400" />
              <span className="flex-1 font-mono text-sm truncate">{fullUrl}</span>
              <Button size="sm" variant="ghost" onClick={copyUrl}>
                <Copy className="w-4 h-4" />
              </Button>
              <Button size="sm" variant="ghost" asChild>
                <a href={fullUrl} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="w-4 h-4" />
                </a>
              </Button>
            </div>
          )}

          {/* QR Code */}
          <div className="pt-4 border-t">
            <Button
              variant="outline"
              onClick={() => setShowQR(!showQR)}
              className="w-full"
            >
              <QrCode className="w-4 h-4 mr-2" />
              {showQR ? "Hide" : "Show"} QR Code
            </Button>
            
            {showQR && fullUrl && (
              <div className="mt-4 flex flex-col items-center">
                <div className="bg-white p-4 rounded-lg shadow-lg">
                  <QRCode value={fullUrl} size={200} level="H" />
                </div>
                <p className="text-sm text-stone-500 mt-2">
                  Print this on business cards & flyers!
                </p>
              </div>
            )}
          </div>

          {/* Share My Site Card Generator */}
          <div className="pt-4 border-t">
            <Button
              onClick={() => setShowShareCard(!showShareCard)}
              className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-black font-semibold"
            >
              <Share2 className="w-4 h-4 mr-2" />
              {showShareCard ? "Hide" : "Create"} Shareable Card
            </Button>
            
            {showShareCard && fullUrl && (
              <div className="mt-4 space-y-4">
                {/* The shareable card */}
                <div 
                  ref={shareCardRef}
                  className="relative mx-auto"
                  style={{ width: '320px', height: '320px' }}
                >
                  {/* Card Design */}
                  <div 
                    className="w-full h-full rounded-2xl overflow-hidden shadow-2xl"
                    style={{ 
                      background: `linear-gradient(135deg, ${settings.custom_branding?.bg_color || '#000'} 0%, #1a1a1a 100%)` 
                    }}
                  >
                    {/* Gold top bar */}
                    <div 
                      className="h-2"
                      style={{ backgroundColor: settings.custom_branding?.primary_color || '#d4af37' }}
                    />
                    
                    {/* Content */}
                    <div className="p-6 flex flex-col items-center text-center">
                      <p 
                        className="text-sm font-medium tracking-wider mb-1"
                        style={{ color: settings.custom_branding?.primary_color || '#d4af37' }}
                      >
                        BOOK WITH
                      </p>
                      <h3 
                        className="text-2xl font-bold mb-4"
                        style={{ color: settings.custom_branding?.secondary_color || '#dc2626' }}
                      >
                        {userName || settings.signature_text || 'Your Name'}
                      </h3>
                      
                      {/* QR Code */}
                      <div className="bg-white p-3 rounded-xl mb-4">
                        <QRCode value={fullUrl} size={140} level="H" />
                      </div>
                      
                      {/* URL */}
                      <p className="text-white/70 text-xs font-mono">
                        {fullUrl.replace('https://', '')}
                      </p>
                      
                      {/* Branding */}
                      <p 
                        className="text-xs mt-3 font-medium"
                        style={{ color: settings.custom_branding?.primary_color || '#d4af37' }}
                      >
                        Powered by MyHairChair
                      </p>
                    </div>
                    
                    {/* Gold bottom bar */}
                    <div 
                      className="absolute bottom-0 left-0 right-0 h-2"
                      style={{ backgroundColor: settings.custom_branding?.primary_color || '#d4af37' }}
                    />
                  </div>
                </div>

                {/* Share buttons */}
                <div className="flex flex-wrap justify-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={async () => {
                      if (!shareCardRef.current) return;
                      setGeneratingCard(true);
                      try {
                        const canvas = await html2canvas(shareCardRef.current, {
                          scale: 2,
                          backgroundColor: null,
                          useCORS: true
                        });
                        const link = document.createElement('a');
                        link.download = `${settings.profile_slug || 'my-minisite'}-qr-card.png`;
                        link.href = canvas.toDataURL('image/png');
                        link.click();
                        toast.success("Card downloaded!");
                      } catch (err) {
                        toast.error("Failed to generate card");
                      } finally {
                        setGeneratingCard(false);
                      }
                    }}
                    disabled={generatingCard}
                  >
                    {generatingCard ? (
                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4 mr-1" />
                    )}
                    Download
                  </Button>
                  
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={async () => {
                      if (!shareCardRef.current) return;
                      setGeneratingCard(true);
                      try {
                        const canvas = await html2canvas(shareCardRef.current, {
                          scale: 2,
                          backgroundColor: null,
                          useCORS: true
                        });
                        canvas.toBlob(async (blob) => {
                          if (blob && navigator.share) {
                            const file = new File([blob], 'my-booking-qr.png', { type: 'image/png' });
                            await navigator.share({
                              title: `Book with ${userName || settings.signature_text}`,
                              text: `Scan to book your next appointment!`,
                              files: [file]
                            });
                          } else {
                            toast.error("Sharing not supported on this device");
                          }
                        }, 'image/png');
                      } catch (err) {
                        if (err.name !== 'AbortError') {
                          toast.error("Failed to share");
                        }
                      } finally {
                        setGeneratingCard(false);
                      }
                    }}
                    disabled={generatingCard}
                  >
                    <Instagram className="w-4 h-4 mr-1" />
                    Share to Story
                  </Button>
                  
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={async () => {
                      if (!shareCardRef.current) return;
                      setGeneratingCard(true);
                      try {
                        const canvas = await html2canvas(shareCardRef.current, {
                          scale: 2,
                          backgroundColor: null,
                          useCORS: true
                        });
                        canvas.toBlob(async (blob) => {
                          if (blob) {
                            const file = new File([blob], 'my-booking-qr.png', { type: 'image/png' });
                            // Download the image first
                            const link = document.createElement('a');
                            link.download = `${settings.profile_slug || 'booking'}-qr.png`;
                            link.href = canvas.toDataURL('image/png');
                            link.click();
                            // Then open SMS
                            setTimeout(() => {
                              window.open(`sms:?&body=Book your next appointment with me! ${fullUrl}`, '_blank');
                            }, 500);
                            toast.success("Image downloaded! Attach it to your message.");
                          }
                        }, 'image/png');
                      } catch (err) {
                        toast.error("Failed to generate card");
                      } finally {
                        setGeneratingCard(false);
                      }
                    }}
                    disabled={generatingCard}
                  >
                    <MessageSquare className="w-4 h-4 mr-1" />
                    Send via Text
                  </Button>
                </div>
                
                <p className="text-xs text-center text-stone-500">
                  Perfect for Instagram Stories, business cards, and flyers!
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Tabs for different settings */}
      <Tabs defaultValue="profile" className="w-full">
        <TabsList className="grid w-full grid-cols-6 mb-4">
          <TabsTrigger value="profile" className="text-xs md:text-sm">
            <User className="w-4 h-4 mr-1 hidden md:inline" />
            Profile
          </TabsTrigger>
          <TabsTrigger value="bulletin" className="text-xs md:text-sm">
            <Megaphone className="w-4 h-4 mr-1 hidden md:inline" />
            Bulletin
          </TabsTrigger>
          <TabsTrigger value="welcome" className="text-xs md:text-sm">
            <Sparkles className="w-4 h-4 mr-1 hidden md:inline" />
            Welcome
          </TabsTrigger>
          <TabsTrigger value="hours" className="text-xs md:text-sm">
            <Clock className="w-4 h-4 mr-1 hidden md:inline" />
            Hours
          </TabsTrigger>
          <TabsTrigger value="policies" className="text-xs md:text-sm">
            <Shield className="w-4 h-4 mr-1 hidden md:inline" />
            Policies
          </TabsTrigger>
          <TabsTrigger value="branding" className="text-xs md:text-sm">
            <Palette className="w-4 h-4 mr-1 hidden md:inline" />
            Branding
          </TabsTrigger>
        </TabsList>

        {/* Bulletin Board Tab */}
        <TabsContent value="bulletin">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Megaphone className="w-5 h-5 text-amber-500" />
                Bulletin Board
              </CardTitle>
              <CardDescription>
                Post announcements, sales, and special messages visible to clients when booking
              </CardDescription>
            </CardHeader>
            <CardContent>
              <BulletinBoard token={token} membershipTier={membershipTier} />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Profile Tab - Image & Gallery */}
        <TabsContent value="profile">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Camera className="w-5 h-5 text-amber-500" />
                Profile Image & Gallery
              </CardTitle>
              <CardDescription>
                Add your photo and showcase your best work (up to 6 images)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Profile Image */}
              <div>
                <Label className="text-base font-semibold mb-3 block">Profile Photo</Label>
                <div className="flex items-start gap-4">
                  <div className="relative">
                    {settings.profile_image ? (
                      <div className="relative group">
                        <img 
                          src={settings.profile_image} 
                          alt="Profile" 
                          className="w-32 h-32 rounded-xl object-cover border-2 border-amber-500/30"
                        />
                        <button
                          onClick={removeProfileImage}
                          className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <div 
                        onClick={() => profileImageRef.current?.click()}
                        className="w-32 h-32 rounded-xl border-2 border-dashed border-stone-300 hover:border-amber-500 flex flex-col items-center justify-center cursor-pointer transition-colors bg-stone-50 hover:bg-amber-50"
                      >
                        {uploadingImage ? (
                          <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
                        ) : (
                          <>
                            <Camera className="w-8 h-8 text-stone-400 mb-2" />
                            <span className="text-xs text-stone-500">Add Photo</span>
                          </>
                        )}
                      </div>
                    )}
                    <input
                      ref={profileImageRef}
                      type="file"
                      accept="image/*"
                      onChange={handleProfileImageUpload}
                      className="hidden"
                    />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-stone-600 mb-2">
                      This is your main profile photo that appears on your mini-site.
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => profileImageRef.current?.click()}
                      disabled={uploadingImage}
                    >
                      {uploadingImage ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Upload className="w-4 h-4 mr-2" />
                      )}
                      {settings.profile_image ? "Change Photo" : "Upload Photo"}
                    </Button>
                  </div>
                </div>
              </div>

              {/* Gallery Images */}
              <div className="pt-4 border-t">
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-base font-semibold">Hairstyle Gallery</Label>
                  <Badge variant="outline" className="text-stone-500">
                    {settings.gallery_images?.length || 0} / 6 images
                  </Badge>
                </div>
                <p className="text-sm text-stone-500 mb-4">
                  Showcase your best work! Add up to 6 photos of hairstyles you&apos;ve created.
                </p>
                
                <div className="grid grid-cols-3 gap-3">
                  {/* Existing gallery images */}
                  {(settings.gallery_images || []).map((url, index) => (
                    <div key={index} className="relative group aspect-square">
                      <img 
                        src={url} 
                        alt={`Gallery ${index + 1}`}
                        className="w-full h-full object-cover rounded-lg border border-stone-200"
                      />
                      <button
                        onClick={() => removeGalleryImage(index)}
                        className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                      <div className="absolute bottom-1 left-1 bg-black/60 text-white text-xs px-2 py-0.5 rounded">
                        {index + 1}
                      </div>
                    </div>
                  ))}
                  
                  {/* Add more images button (if under limit) */}
                  {(settings.gallery_images?.length || 0) < 6 && (
                    <div 
                      onClick={() => galleryInputRef.current?.click()}
                      className="aspect-square rounded-lg border-2 border-dashed border-stone-300 hover:border-amber-500 flex flex-col items-center justify-center cursor-pointer transition-colors bg-stone-50 hover:bg-amber-50"
                    >
                      {uploadingGallery ? (
                        <Loader2 className="w-6 h-6 text-amber-500 animate-spin" />
                      ) : (
                        <>
                          <Plus className="w-6 h-6 text-stone-400 mb-1" />
                          <span className="text-xs text-stone-500">Add Image</span>
                        </>
                      )}
                    </div>
                  )}
                </div>
                
                <input
                  ref={galleryInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleGalleryUpload}
                  className="hidden"
                />
                
                <p className="text-xs text-stone-400 mt-3">
                  Tip: Use high-quality, well-lit photos that showcase your style. Max 5MB per image.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Welcome Message Tab */}
        <TabsContent value="welcome">
          <Card>
            <CardHeader>
              <CardTitle>Welcome Message & Signature</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Welcome Message</Label>
                <Textarea
                  value={settings.welcome_message}
                  onChange={(e) => setSettings(prev => ({ ...prev, welcome_message: e.target.value }))}
                  placeholder="Welcome to my chair! I specialize in..."
                  className="mt-1"
                  rows={3}
                />
              </div>
              
              <div>
                <Label>Signature Text</Label>
                <Input
                  value={settings.signature_text}
                  onChange={(e) => setSettings(prev => ({ ...prev, signature_text: e.target.value }))}
                  placeholder="Your name/nickname"
                  className="mt-1 font-script text-xl"
                />
                <p className="text-xs text-stone-500 mt-1">
                  This appears as a stylized signature on your mini-site
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t">
                <div>
                  <Label>Instagram Handle</Label>
                  <Input
                    value={settings.social_links?.instagram || ""}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      social_links: { ...prev.social_links, instagram: e.target.value }
                    }))}
                    placeholder="@yourhandle"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    value={settings.social_links?.email || ""}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      social_links: { ...prev.social_links, email: e.target.value }
                    }))}
                    placeholder="you@email.com"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input
                    value={settings.social_links?.phone || ""}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      social_links: { ...prev.social_links, phone: e.target.value }
                    }))}
                    placeholder="(555) 123-4567"
                    className="mt-1"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Business Hours Tab */}
        <TabsContent value="hours">
          <Card>
            <CardHeader>
              <CardTitle>Business Hours</CardTitle>
              <CardDescription>Set your weekly availability</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map(day => (
                  <div key={day} className="flex items-center gap-3">
                    <span className="w-24 font-medium capitalize">{day}</span>
                    <Input
                      value={settings.business_hours?.[day] || "CLOSED"}
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        business_hours: { ...prev.business_hours, [day]: e.target.value }
                      }))}
                      placeholder="9:00 AM - 5:00 PM"
                      className="flex-1"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSettings(prev => ({
                        ...prev,
                        business_hours: { ...prev.business_hours, [day]: "CLOSED" }
                      }))}
                      className="text-xs"
                    >
                      Closed
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Booking Policies Tab */}
        <TabsContent value="policies">
          <Card>
            <CardHeader>
              <CardTitle>Booking Policies</CardTitle>
              <CardDescription>Set your cancellation, deposit, and late policies</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Cancellation Notice Required</Label>
                  <Input
                    value={settings.booking_policies?.cancellation_notice || ""}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      booking_policies: { ...prev.booking_policies, cancellation_notice: e.target.value }
                    }))}
                    placeholder="2 hours"
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label>Deposit Amount ($)</Label>
                  <Input
                    type="number"
                    value={settings.booking_policies?.deposit_amount || 0}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      booking_policies: { ...prev.booking_policies, deposit_amount: parseFloat(e.target.value) || 0 }
                    }))}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label>Service Fee ($)</Label>
                  <Input
                    type="number"
                    value={settings.booking_policies?.deposit_service_fee || 0}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      booking_policies: { ...prev.booking_policies, deposit_service_fee: parseFloat(e.target.value) || 0 }
                    }))}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label>Late Grace Period (minutes)</Label>
                  <Input
                    type="number"
                    value={settings.booking_policies?.late_grace_period || 10}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      booking_policies: { ...prev.booking_policies, late_grace_period: parseInt(e.target.value) || 10 }
                    }))}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label>Late Fee ($)</Label>
                  <Input
                    type="number"
                    value={settings.booking_policies?.late_fee || 0}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      booking_policies: { ...prev.booking_policies, late_fee: parseFloat(e.target.value) || 0 }
                    }))}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label>Cancel After (minutes late)</Label>
                  <Input
                    type="number"
                    value={settings.booking_policies?.late_cancel_minutes || 15}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      booking_policies: { ...prev.booking_policies, late_cancel_minutes: parseInt(e.target.value) || 15 }
                    }))}
                    className="mt-1"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Custom Branding Tab (Elite Only) */}
        <TabsContent value="branding">
          <Card className={!isElite ? "opacity-60" : ""}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                Custom Branding
                {!isElite && (
                  <Badge className="bg-purple-500 text-white text-xs">
                    Elite Only
                  </Badge>
                )}
              </CardTitle>
              <CardDescription>
                Customize your mini-site colors to match your brand
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {!isElite ? (
                <div className="text-center py-8">
                  <Crown className="w-12 h-12 text-purple-500 mx-auto mb-4" />
                  <p className="text-stone-600 mb-4">
                    Upgrade to Elite to customize your mini-site colors and remove MyHairChair branding.
                  </p>
                  <Badge className="bg-purple-500 text-white">
                    Upgrade to Elite
                  </Badge>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label>Primary Color (Gold accents)</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <input
                        type="color"
                        value={settings.custom_branding?.primary_color || "#d4af37"}
                        onChange={(e) => setSettings(prev => ({
                          ...prev,
                          custom_branding: { ...prev.custom_branding, primary_color: e.target.value }
                        }))}
                        className="w-12 h-10 rounded cursor-pointer"
                      />
                      <Input
                        value={settings.custom_branding?.primary_color || "#d4af37"}
                        onChange={(e) => setSettings(prev => ({
                          ...prev,
                          custom_branding: { ...prev.custom_branding, primary_color: e.target.value }
                        }))}
                        className="flex-1 font-mono"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label>Secondary Color (Headers)</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <input
                        type="color"
                        value={settings.custom_branding?.secondary_color || "#dc2626"}
                        onChange={(e) => setSettings(prev => ({
                          ...prev,
                          custom_branding: { ...prev.custom_branding, secondary_color: e.target.value }
                        }))}
                        className="w-12 h-10 rounded cursor-pointer"
                      />
                      <Input
                        value={settings.custom_branding?.secondary_color || "#dc2626"}
                        onChange={(e) => setSettings(prev => ({
                          ...prev,
                          custom_branding: { ...prev.custom_branding, secondary_color: e.target.value }
                        }))}
                        className="flex-1 font-mono"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label>Background Color</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <input
                        type="color"
                        value={settings.custom_branding?.bg_color || "#000000"}
                        onChange={(e) => setSettings(prev => ({
                          ...prev,
                          custom_branding: { ...prev.custom_branding, bg_color: e.target.value }
                        }))}
                        className="w-12 h-10 rounded cursor-pointer"
                      />
                      <Input
                        value={settings.custom_branding?.bg_color || "#000000"}
                        onChange={(e) => setSettings(prev => ({
                          ...prev,
                          custom_branding: { ...prev.custom_branding, bg_color: e.target.value }
                        }))}
                        className="flex-1 font-mono"
                      />
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button
          onClick={saveSettings}
          disabled={saving || slugAvailable === false}
          className="bg-amber-500 hover:bg-amber-600 text-black px-8"
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            "Save Mini-Site Settings"
          )}
        </Button>
      </div>
    </div>
  );
};

export default MiniSiteSettings;
